import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { copyTextToClipboard } from '@/lib/copyToClipboard';

import { fetchAllTranscriptSegments } from '../api';
import {
  buildPlainTranscriptExport,
  type MeetingExportContext,
} from '../buildTranscriptExport';

const COPIED_RESET_MS = 1800;

type Props = {
  transcriptionId: string;
  meeting: MeetingExportContext;
  enabled: boolean;
};

/**
 * Второстепенное копирование расшифровки без промта.
 * Работа с заданиями ChatGPT — в блоке «Следующий шаг».
 */
export function TranscriptExportActions({ transcriptionId, meeting, enabled }: Props) {
  const busyRef = useRef(false);
  const [copyLabel, setCopyLabel] = useState<'idle' | 'copied'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (copyLabel !== 'copied') {
      return;
    }
    const timer = window.setTimeout(() => setCopyLabel('idle'), COPIED_RESET_MS);
    return () => window.clearTimeout(timer);
  }, [copyLabel]);

  const onCopyPlain = async () => {
    if (!enabled || busyRef.current) {
      return;
    }
    busyRef.current = true;
    setError(null);
    try {
      const segments = await fetchAllTranscriptSegments(transcriptionId);
      if (segments.length === 0) {
        setError('Текст расшифровки отсутствует');
        return;
      }
      const text = buildPlainTranscriptExport(meeting, segments);
      await copyTextToClipboard(text);
      setCopyLabel('copied');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось скопировать');
    } finally {
      busyRef.current = false;
    }
  };

  return (
    <div className="transcript-export">
      <div className="transcript-export__actions">
        <Button
          type="button"
          variant="secondary"
          disabled={!enabled}
          onClick={() => {
            void onCopyPlain();
          }}
        >
          {copyLabel === 'copied' ? (
            <>
              <span aria-hidden="true">✓</span> Скопировано
            </>
          ) : (
            'Скопировать расшифровку'
          )}
        </Button>
      </div>

      {error !== null ? (
        <p className="transcript-export__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
